import produce from 'immer';
import createCachedSelector from 're-reselect';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';
import { getType } from 'typesafe-actions';
import { actions } from '..';
import { secondsToTimestamp } from '../../api';
import { getQueueMode } from '../../api/queue-control';
import { EntityType, getKey, GosGraphType, GosLimit, Identifier, QueueIconEnum, QueueRegion, QueueRegionId, QueueStats, QueueView, sameRegion } from '../../model';
import { isDefined } from '../../type-utils';
import { Actions, StoreState } from '../types';
import { selectConfigProp, selectSingleQueueOrSquad } from './config';
import { selectSelectedQueueView } from './localSettings';

type Queues = null | { [key: string]: QueueStats | undefined }
type QueueKeys = string[] | null

function readQueues(queues: QueueStats[], state: NonNullable<Queues>): Queues {
  const newState: Queues = {}
  for (const queue of queues) {
    newState[getKey(queue)] = queue
  }
  const s = { ...state, ...newState }
  return s
}

const queueUpdateKeys = ['icon', 'id', 'server', 'description', 'mode', 'status', 'count', 'time', 'agents', 'answer', 'abandoned', 'other', 'agentStatus', 'virtual'] as const

function queues(state: Queues = null, action: Actions): Queues {
  switch (action.type) {
    case getType(actions.fetchQueue.response): {
      return {
        ...state,
        [getKey(action.payload)]: action.payload,
      }
    }

    case getType(actions.fetchQueues.response): {
      if (action.payload.region === QueueRegionId.Squads) {
        return state
      }

      return readQueues(action.payload.queues, state || {})
    }

    case getType(actions.fetchFavQueues.response): {
      return readQueues(action.payload, state || {})
    }

    case getType(actions.fetchMyQueues.response): {
      return readQueues(action.payload, state || {})
    }

    case getType(actions.updateQueue): {
      // don't add new queues to the store
      const { icon, id, server, description, mode, status, count, time, agents, answer, abandoned, other, agentStatus, virtual, ansQTimeTotal, abnQTimeTotal } = action.payload
      if (state) {
        const data = state[getKey(action.payload)]
        if (data === undefined) {
          const queue: QueueStats = {
            type: 'queue',
            icon: icon,
            id: id,
            server: server,
            description: description,
            mode: mode,
            status: status,
            count: count,
            time: time,
            agents: agents,
            answer: answer,
            abandoned: abandoned,
            other: other,
            agentStatus: Math.round(agentStatus),
            virtual: virtual,
            voicemailCount: 0,
            ansQTimeTotal,
            abnQTimeTotal,
            toaster: icon,
            hide: false,
          }

          return {
            ...state,
            [getKey(action.payload)]: {
              ...queue,
            },
          }
        } else {

          if (queueUpdateKeys.every(prop => data[prop] === action.payload[prop])) {
            return state
          }

          const toaster = (data.toaster === QueueIconEnum.OpenWarning || data.toaster === QueueIconEnum.OpenAlert) &&
            icon !== QueueIconEnum.OpenWarning &&
            icon !== QueueIconEnum.OpenAlert ?
            icon : data.toaster

          return {
            ...state,
            [getKey(action.payload)]: {
              ...data,
              icon,
              id,
              server,
              description,
              mode,
              status,
              count,
              time,
              agents,
              answer,
              abandoned,
              other,
              agentStatus,
              virtual,
              toaster: toaster,
              hide: data.hide,  // stays the same and is only changed my alert broadcasts
            },
          }
        }
      }

      return state
    }
    case getType(actions.closeAlertQueue):
      {
        const key = getKey(action.payload)
        const queue = state && state[key]
        if (!queue) { return state }

        return {
          ...state,
          [key]: {
            ...queue,
            hide: true,
          },
        }
      }

    case getType(actions.shutdown):
      {
        return produce(state, state => {
          for (const key in state) {
            if (Object.prototype.hasOwnProperty.call(state, key)) {
              const element = state[key];
              if (element)
                element.hide = true;
            }
          }
        })
      }

    case getType(actions.setQueuePopIcon): {
      const { queue, icon } = action.payload
      const key = getKey(queue)
      const q = state && state[key]
      if (!q) { return state }

      const hide = q.icon === icon ? q.hide : false;

      return {
        ...state,
        [key]: {
          ...q,
          toaster: icon,
          hide,
        },
      }
    }

    case getType(actions.setQueueIcon): {
      const { queue, icon } = action.payload
      const key = getKey(queue)
      const q = state && state[key]

      if (!q) { return state }
      if (q.icon === icon) { return state }

      return {
        ...state,
        [key]: {
          ...q,
          icon: icon,
        },
      }
    }

    case getType(actions.setConfig): {
      return produce(state, state => {
        const { shortcuts } = action.payload

        for (let index = 0; index < shortcuts.length; index++) {
          const button = shortcuts[index];
          if (button.entityType === EntityType.Queue) {
            const ext = { server: button.serverName, id: button.entityId }
            const key = getKey(ext)
            const data = button.data
            if (state && data) {
              state[key] = {
                type: 'queue',
                icon: parseInt(data[0]),
                id: button.entityId,
                server: button.serverName,
                description: button.description,
                mode: getQueueMode(parseInt(data[1])),
                status: data[2],
                count: parseInt(data[3]),
                time: data[3] !== '' ? secondsToTimestamp(parseInt(data[4])) : null,
                agents: parseInt(data[5]),
                answer: parseInt(data[6]),
                abandoned: parseInt(data[7]),
                other: parseInt(data[8]),
                agentStatus: parseInt(data[9]),
                virtual: data[10] === '1',
                voicemailCount: 0,
                ansQTimeTotal: parseInt(data[11]),
                abnQTimeTotal: parseInt(data[12]),
                toaster: parseInt(data[0]),
                hide: false,
              }
            }
          }
        }
      })
    }

    case getType(actions.updateVoicemailCount): {
      const key = getKey(action.payload.id)
      const queue = state && state[key]
      if (!queue) { return state }
      return {
        ...state,
        [key]: {
          ...queue,
          voicemailCount: action.payload.unreadCount,
        },
      }
    }

    case getType(actions.fetchQueuesByIdsResponse): {
      return readQueues(action.payload.queues, state || {})
    }

    case getType(actions.forceReload): {
      return null
    }

    default:
      return state
  }
}

function curQueues(state: QueueKeys = null, action: Actions): QueueKeys {
  switch (action.type) {
    case getType(actions.fetchQueue.response): {
      return [getKey(action.payload)]
    }

    case getType(actions.fetchQueues.response): {
      if (action.payload.region === QueueRegionId.Squads) {
        return state
      }

      return action.payload.queues.map(getKey)
    }

    default:
      return state
  }
}

function favQueues(state: QueueKeys = null, action: Actions): QueueKeys {
  if (action.type === getType(actions.fetchFavQueues.response)) {
    return action.payload.map(getKey)
  }

  if (action.type === getType(actions.favouriteQueueAction)) {
    const { add, queue } = action.payload
    const key = getKey(queue)
    if (state) {
      if (!add) {
        return state.filter(k => k !== key)
      } else {
        if (!state.find(k => k === key)) {
          return state = [...state, key]
        }
      }
    }
  }

  return state
}

function myQueues(state: QueueKeys = null, action: Actions): QueueKeys {
  if (action.type === getType(actions.fetchMyQueues.response)) {
    return action.payload.map(getKey)
  }

  return state
}

function region(state: QueueRegion | null = null, action: Actions) {
  switch (action.type) {
    case getType(actions.fetchQueues.response): {
      return action.payload.region
    }

    default:
      return state
  }
}

function virtualQueues(state: boolean = false, action: Actions) {
  switch (action.type) {
    case getType(actions.fetchQueues.response): {
      return action.payload.region === QueueRegionId.Squads ? false : action.payload.virtual
    }

    default:
      return state
  }
}

function gosLimits(state: GosLimit[] = [], action: Actions): GosLimit[] {
  switch (action.type) {
    case getType(actions.fetchGosLimits.response): {
      return action.payload
    }

    case getType(actions.updateGosLimits): {
      const { limits: [green, yellow, red], queueId, type } = action.payload
      return [...state.filter(x => !(x.queueId === queueId && x.type === type)), {
        queueId,
        type,
        red,
        yellow,
        green,
      }]
    }
  }

  return state
}

export default combineReducers({
  queues,
  curQueues,
  favQueues,
  myQueues,
  region,
  virtualQueues,
  gosLimits,
})

function getRoot(state: StoreState) { return state.queues }
function getRegion(state: StoreState) { return getRoot(state).region }
const getVirtualQueues = (state: StoreState) => getRoot(state).virtualQueues
const getQueues = (state: StoreState) => getRoot(state).queues
const getCurQueues = (state: StoreState) => getRoot(state).curQueues
const getFavQueues = (state: StoreState) => getRoot(state).favQueues
const getMyQueues = (state: StoreState) => getRoot(state).myQueues
const getGosLimits = (state: StoreState) => getRoot(state).gosLimits

export const selectQueuesAreLoading = (state: StoreState, region: QueueRegion) =>
  (sameRegion(region, QueueRegionId.Favourites) ? getFavQueues(state) : getCurQueues(state)) === null

export const selectQueueViewIsLoading = (state: StoreState, view: QueueView) =>
  view.region ? selectQueuesAreLoading(state, view.region) : false

const emptyArr: never[] = []
export const selectQueues = createSelector(
  getQueues,
  getCurQueues,
  getFavQueues,
  getRegion,
  (_s: StoreState, region: QueueRegion) => region,
  (queues, curQueues, favQueues, curRegion, region) => {
    const qs = sameRegion(region, QueueRegionId.Favourites) ? favQueues
      : curRegion && sameRegion(curRegion, region) ? curQueues
        : null

    return qs && queues ? qs.map(k => queues[k]).filter(isDefined) : emptyArr
  },
)

export const selectQueuesInView = createSelector(
  getQueues,
  (state: StoreState) => {
    const single = selectSingleQueueOrSquad(state)
    if (single) {
      return single
    }

    const view = selectSelectedQueueView(state)
    if (view.region) {
      return selectQueues(state, view.region)
    } else {
      return view.queue
    }
  },
  (queues, idOrQueues) => {
    if (Array.isArray(idOrQueues))
      return idOrQueues

    const queue = queues && queues[getKey(idOrQueues)]
    return queue ? [queue] : []
  },
)

export const selectFavQueues = createSelector(
  getQueues,
  getFavQueues,
  (queues, favQueues) => {
    return queues && favQueues ? favQueues.map(k => queues[k]).filter(isDefined) : emptyArr
  },
)

export const selectMyQueues = createSelector(
  getQueues,
  getMyQueues,
  (queues, myQueues) => {
    return queues && myQueues ? myQueues.map(k => queues[k]).filter(isDefined) : emptyArr
  },
)


export const selectMyQueueAlerts = createSelector(
  getQueues,
  (s: StoreState) => selectConfigProp(s, 'myQueues'),
  (s: StoreState) => selectConfigProp(s, 'serverName'),
  (queues, myQueues, serverName) => {
    return queues && myQueues && serverName ? myQueues.map(k => queues[getKey({ server: serverName, id: k })]).filter(m => m !== undefined && (m.toaster === QueueIconEnum.OpenAlert || m.toaster === QueueIconEnum.OpenWarning) && m.hide === false) : emptyArr
  },
)

export function shouldFetchQueues(state: StoreState, region: QueueRegion, virtual: boolean) {
  const curRegion = getRegion(state)
  const curVirtual = getVirtualQueues(state)

  if (sameRegion(region, QueueRegionId.Favourites)) { return virtual !== curVirtual }
  return curRegion === null
    || getQueues(state) === null
    || !sameRegion(region, curRegion)
    || (region !== QueueRegionId.Squads && virtual !== curVirtual)
}

export const selectQueue = createCachedSelector(
  getQueues,
  (_s: StoreState, queueId: Identifier) => queueId.server,
  (_s: StoreState, queueId: Identifier) => queueId.id,
  (queues, server, id) => {
    if (!queues) return undefined

    return queues[getKey({ id, server })]
  },
)(
  (_s, queueId) => getKey(queueId),
)

export const selectGosLimits = createCachedSelector(
  getGosLimits,
  (_state: StoreState, { queueId }: { queueId: string, type: GosGraphType }) => queueId,
  (_state, { type }) => type,
  (limits, queueId, type) => {
    const limit = limits.find(x => x.queueId === queueId && x.type === type)
    if (!limit) { return null }
    return [limit.green, limit.yellow, limit.red] as const
  },
)(
  (_state, { queueId, type }) => `${queueId}-${type}`,
)

export const selectAllGosLimits = createCachedSelector(
  getGosLimits,
  (_state: StoreState, queueId: string) => queueId,
  (limits, queueId) => {
    return limits.filter(x => x.queueId === queueId)
  },
)(
  (_state, queueId) => queueId,
)
